import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { QuestionItem } from "../../../components/Quest/QuestionItem";
import images from "../../../images";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useGetListObjectivesQuestionQuery,
  usePostAIGenQuestionMutation,
  usePostCalculatorIncentiveMutation,
} from "../../../services/apiSlice";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";

import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import {
  updateQuestionAI,
  setIncentiveRedux,
} from "../../../redux/featuresSlice/commonSlice";
import { useTranslation } from "react-i18next";
import { formatQuestionType } from "../../../Utils/helper";
const StepWhatsAsk = ({
  selectedObjectiveId,
  selectedObjectiveName,
  selectedObjectiveStatus,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const hasCustomQuestMaual = queryParams.has("customQuestMaual");
  const [hasTypeBack, setHasTypeBack] = useState(false);

  const {
    questionsDataOndemandCustom,
    questionsDataOndemandDefault: rawQuestionsDataOndemandDefault,
    questionsDataOndemandAIGen,
  } = useSelector((state) => state.common);
  const { objectiveNameRedux } = useSelector((state) => state.common);

  // Memoize questionsDataOndemandDefault to stabilize its reference
  const questionsDataOndemandDefault = useMemo(
    () =>
      selectedObjectiveStatus === "customManual"
        ? []
        : rawQuestionsDataOndemandDefault,
    [selectedObjectiveStatus, rawQuestionsDataOndemandDefault]
  );

  const [state] = useState({
    page: 1,
    limit: 5,
    status: "",
    selectedObjective: null,
  });

  const [incentive, setIncentive] = useState(2);
  const hasGeneratedInitialQuestions = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const prevQuestionCountsRef = useRef(null);

  const {
    isLoading: isLoadingObjectiveQuestionData,
    isSuccess: isSuccessObjectiveQuestionData,
  } = useGetListObjectivesQuestionQuery(
    {
      limit: state.limit,
      page: state.page,
      objective_id: selectedObjectiveId,
    },
    {
      skip:
        !selectedObjectiveId ||
        selectedObjectiveStatus === "customManual" ||
        hasCustomQuestMaual === true,
    }
  );

  const [postAIGenQuestion, { isLoading: isLoadingAIGenQuestion }] =
    usePostAIGenQuestionMutation();

  const [postCalculatorIncentive, { isLoading: isLoadingCalculatorIncentive }] =
    usePostCalculatorIncentiveMutation();

  // Sync hasTypeBack with location.search
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const typeValue = queryParams.get("type");
    setHasTypeBack(typeValue === "back");
  }, [location.search]);

  // Memoize onAnswerQuestion and onAnswerQuestionAI
  const onAnswerQuestion = useCallback(
    (type, question, isDefault = false) => {
      const typeParam = type.toLowerCase();
      const queryString = isDefault
        ? `type=${typeParam}&defaultQuestionCustome`
        : `type=${typeParam}&customQuest`;
      navigate(`/ondemand/question?${queryString}`, {
        state: {
          question,
          objecttive_name: selectedObjectiveName || "",
        },
      });
    },
    [navigate, selectedObjectiveName]
  );

  const onAnswerQuestionAI = useCallback(
    (type, question) => {
      const typeParam = type ? type.toLowerCase() : question.type.toLowerCase();
      navigate(`/ondemand/question?type=${typeParam}&customQuest`, {
        state: {
          question,
          objecttive_name: selectedObjectiveName || "",
          isAIGenerated: true,
        },
      });
    },
    [navigate, selectedObjectiveName]
  );

  const allQuestions = useMemo(() => {
    const defaultQuestions = (questionsDataOndemandDefault || []).map((q) => ({
      ...q,
      source: "default",
      createdAt: q.createdAt || 0, // luôn ở trên cùng
      questionText: q.objective_question || q.question,
      typeLabel: q.type,
      onClick: () => onAnswerQuestion(q.type, q, true),
    }));

    const aiQuestions = (questionsDataOndemandAIGen || []).map((q) => ({
      ...q,
      source: "ai",
      createdAt: q.createdAt,
      questionText: q.question_text || q.question || q.objective_question,
      typeLabel: q.question_type || q.type,
      onClick: () => onAnswerQuestionAI(q.question_type, q),
    }));

    const customQuestions = (questionsDataOndemandCustom || []).map((q) => ({
      ...q,
      source: "custom",
      createdAt: q.createdAt,
      questionText: q.question,
      typeLabel: q.type,
      onClick: () => onAnswerQuestion(q.type, q),
    }));

    const otherQuestions = [...aiQuestions, ...customQuestions].sort(
      (a, b) => a.createdAt - b.createdAt
    );

    return [...defaultQuestions, ...otherQuestions];
  }, [
    questionsDataOndemandDefault,
    questionsDataOndemandAIGen,
    questionsDataOndemandCustom,
    onAnswerQuestion,
    onAnswerQuestionAI,
  ]);

  const calculateQuestionCounts = useCallback(() => {
    const questionCounts = {
      mcq: 0,
      yes_no: 0,
      checkbox: 0,
      slider: 0,
      short: 0,
      long: 0,
      additional_comment: 0,
    };

    allQuestions.forEach((q) => {
      const type = q.typeLabel.toLowerCase();
      if (type && questionCounts[type] !== undefined) {
        questionCounts[type]++;
      }
      if (q.isAdditionalComments === true && q.compulsory === true) {
        questionCounts.additional_comment++;
      }
    });
    return questionCounts;
  }, [allQuestions]);

  const generateInitialAIQuestions = useCallback(async () => {
    try {
      const body = {
        objective: selectedObjectiveName || objectiveNameRedux,
        previous_questions: [],
      };
      const response = await postAIGenQuestion(body).unwrap();

      if (response.message === "List successful" || response.status === 200) {
        const updatedData = response.data?.map((item) => ({
          ...item,
          id: uuidv4(),
        }));
        dispatch(updateQuestionAI(updatedData));
      }
    } catch (error) {
      toast.error(t("toast.error.failedToGenerateInitialQuestions"));
    }
  }, [
    selectedObjectiveName,
    objectiveNameRedux,
    postAIGenQuestion,
    dispatch,
    t,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const shouldGenerateCustom =
        selectedObjectiveStatus === "customManual" &&
        !hasGeneratedInitialQuestions.current;

      const shouldGenerateDefault =
        selectedObjectiveStatus !== "customManual" &&
        isSuccessObjectiveQuestionData &&
        !isLoadingObjectiveQuestionData &&
        questionsDataOndemandDefault.length === 0 &&
        questionsDataOndemandAIGen.length === 0 &&
        questionsDataOndemandCustom.length === 0 &&
        !hasGeneratedInitialQuestions.current;

      if ((shouldGenerateCustom || shouldGenerateDefault) && !hasTypeBack) {
        generateInitialAIQuestions();
        hasGeneratedInitialQuestions.current = true;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [
    isSuccessObjectiveQuestionData,
    isLoadingObjectiveQuestionData,
    questionsDataOndemandDefault,
    questionsDataOndemandAIGen,
    questionsDataOndemandCustom,
    selectedObjectiveStatus,
    hasTypeBack,
    generateInitialAIQuestions,
  ]);

  useEffect(() => {
    const fetchIncentive = async (questionCounts) => {
      try {
        const body = questionCounts;
        const response = await postCalculatorIncentive(body).unwrap();

        if (
          response?.message === "Operation successful" ||
          response.status === 200
        ) {
          dispatch(setIncentiveRedux(response?.data.incentives));
          setIncentive(response?.data.incentives);
        } else {
          toast.warn(t("toast.warn.incentiveValueNotFoundInResponse"));
        }
      } catch (error) {
        toast.error(t("toast.error.failedToCalculateIncentive"));
      }
    };

    if (allQuestions.length > 0) {
      const questionCounts = calculateQuestionCounts();
      const prevCounts = prevQuestionCountsRef.current;

      // Convert to string for deep comparison
      const currentCountsStr = JSON.stringify(questionCounts);
      const prevCountsStr = JSON.stringify(prevCounts);

      // Only fetch if counts have changed
      if (currentCountsStr !== prevCountsStr) {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
          fetchIncentive(questionCounts);
          prevQuestionCountsRef.current = questionCounts; // Update after fetch
        }, 100); // 100ms debounce delay
      }
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [
    allQuestions,
    postCalculatorIncentive,
    dispatch,
    calculateQuestionCounts,
    t,
  ]);

  const handleGenerateMoreQuestion = async () => {
    try {
      const objectiveQuestionsDefaultArr = questionsDataOndemandDefault.map(
        (item) => item.objective_question || item.question
      );
      const objectiveQuestionsCustomArr = questionsDataOndemandCustom.map(
        (item) => item.question
      );
      const objectiveQuestionsAIGenArr = questionsDataOndemandAIGen.map(
        (item) => item.objective_question || item.question || item.question_text
      );

      const objectiveQuestionsCombitionArr = [
        ...objectiveQuestionsDefaultArr,
        ...objectiveQuestionsCustomArr,
        ...objectiveQuestionsAIGenArr,
      ];

      const body = {
        objective: selectedObjectiveName || objectiveNameRedux,
        previous_questions: objectiveQuestionsCombitionArr,
      };
      const response = await postAIGenQuestion(body).unwrap();

      if (response.message === "List successful" || response.status === 200) {
        const updatedData = response.data?.map((item) => ({
          ...item,
          id: uuidv4(),
          createdAt: Date.now(),
        }));
        dispatch(
          updateQuestionAI([...questionsDataOndemandAIGen, ...updatedData])
        );
      }
    } catch (error) {
      toast.error(t("toast.error.failedToFetchAIGenQuestions"));
    }
  };

  const handleCustomQuestionClick = () => {
    navigate("/ondemand/question?type=long", {
      state: {
        question: {
          id: uuidv4(),
          createdAt: Date.now(),
          objective_question: "",
          options: [],
          objective_id: selectedObjectiveId || "",
          objecttive_name: selectedObjectiveName || "",
        },
        objecttive_name: selectedObjectiveName || "",
      },
    });
  };

  return (
    <>
      <LoadingCommon
        statusLoading={
          isLoadingObjectiveQuestionData ||
          isLoadingAIGenQuestion ||
          isLoadingCalculatorIncentive
        }
      />
      <section className="flex flex-col gap-4 p-3 bg-white rounded-xl w-[396px] mx-auto max-md:my-0 max-md:w-full max-md:max-w-[396px] max-sm:p-2 mb-4">
        <h2 className="mb-4 text-lg font-semibold text-zinc-800">
          {selectedObjectiveName}
        </h2>
        {allQuestions.map((question, index) => (
          <div
            key={question.id || question.questionId || index}
            className={index > 0 ? "mt-4" : ""}
          >
            <QuestionItem
              {...question}
              name={`Question ${index + 1}`}
              type={formatQuestionType(question.typeLabel)}
              question={question.questionText}
              // onClick={question.onClick}
              typeLabel={question.typeLabel}
              options={question.options || []}
              handleEditQuest={
                question.onClick ||
                (() =>
                  console.warn(
                    `No edit handler for question ${question.id || index}`
                  ))
              }
            />
          </div>
        ))}
        <section className="p-3 text-center bg-yellow-50 rounded-xl border border-solid border-stone-200">
          <h3 className="mb-2 text-sm text-neutral-500">
            {t("quest.incentive")}
          </h3>
          <p className="text-2xl text-zinc-800">$ {incentive}</p>
        </section>
        <button
          onClick={handleGenerateMoreQuestion}
          className="flex justify-between items-center gap-3 px-6 py-4 rounded-full text-white text-sm font-medium bg-gradient-to-r from-green-400 to-blue-500"
        >
          <img
            src={images.GenAIIconWhite}
            alt="Generate"
            className="w-4 h-4 mr-2"
          />
          <span>{t("quest.generateQuestion")}</span>
          <div></div>
        </button>
        <button
          onClick={handleCustomQuestionClick}
          className="flex items-center justify-between w-full rounded-full px-6 py-4 text-indigo-600 text-sm bg-gray-200"
        >
          <img
            src={images.PencilIconBlue}
            alt="Manual"
            className="w-4 h-4 mr-2"
          />
          <span>{t("quest.customQuestion")}</span>

          <div></div>
        </button>
      </section>
    </>
  );
};

export default StepWhatsAsk;
